import useI18n from '@/hooks/useI18n';
import { useContext, useMemo } from 'react';
import { CalendarFilter } from '@/components/common/CalendarFilter/CalendarFilter';
import {
  useCampaignChartQuery,
  useCampaignRoasChartQuery,
  ValueVsSpendMetrics,
} from '@/hooks/queries/useAnalyticsQueries';
import { UserContext } from '@/context/UserContext';
import ValueVsSpendComponent from './BrandValueVsSpendComponent';
import CampaignOverViewComponent from './CampaignOverViewComponent';
import ChartAnaylysisComponent from './ChartAnaylysisComponent';
import { DUMMY_VALUE_VS_SPEND_INDIVIDUAL } from './dummyAnalytics';
import {
  useDateRangeQueryParams,
  type DateFilteredData,
} from '@/hooks/useDateRangeQueryParams';

interface AnalyticsComponentProps {
  campaign: any;
  dateFilteredData: DateFilteredData;
  onDateFilterChange: (
    startDate: Date | null,
    endDate: Date | null,
    filter: string,
  ) => void;
}

const AnalyticsComponent = ({
  campaign,
  dateFilteredData,
  onDateFilterChange,
}: AnalyticsComponentProps) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;

  const { filter, customStartDate, customEndDate } =
    useDateRangeQueryParams(dateFilteredData);

  const campaignId = campaign?._id || '';
  const isRealUser = !isDummyUser && !!campaignId && !!filter;

  const { data: chartResponse } = useCampaignChartQuery(
    campaignId,
    filter,
    isRealUser,
    customStartDate,
    customEndDate,
  );
  const { data: roasResponse } = useCampaignRoasChartQuery(
    campaignId,
    filter,
    isRealUser,
    customStartDate,
    customEndDate,
  );

  const valueVsSpend = useMemo<ValueVsSpendMetrics>(() => {
    if (isDummyUser) return DUMMY_VALUE_VS_SPEND_INDIVIDUAL;
    return {
      roi: chartResponse?.roi ?? 0,
      averageRoas: chartResponse?.averageRoas ?? 0,
      totalCampaignValue: chartResponse?.totalCampaignValue ?? '0',
      cgc: chartResponse?.cgc ?? '0',
      totalEmv: chartResponse?.totalEmv ?? '0',
      totalSales: chartResponse?.totalSales ?? '0',
      totalSpent: Number(chartResponse?.totalSpent ?? 0),
      approvedCashbackCount: chartResponse?.approvedCashbackCount ?? 0,
    };
  }, [chartResponse, isDummyUser]);

  // ROAS = totalSales / totalSpent = 4600 / 1840 = 2.5. The blended average is
  // the accurate headline; the per-platform/per-bucket series vary around it
  // for illustration (no per-platform spend/sales split exists in the demo).
  const generateDummyROAS = (labels: string[]) => ({
    labels,
    allRoas: [2.3, 2.6, 2.4, 2.7, 2.5, 2.5],
    instagramRoas: [2.6, 2.9, 2.7, 3.0, 2.8, 2.8],
    tiktokRoas: [1.9, 2.2, 2.0, 2.3, 2.1, 2.1],
    averageRoasOfTotal: 2.5,
    averageRoasOfInstagram: 2.8,
    averageRoasOfTiktok: 2.1,
  });

  // Chart series must reconcile with the Value-vs-Spend headline totals:
  // spend 1,840 · EMV 17,733.54 · sales 4,600. Distribute each total across the
  // buckets using the same spike weights so the bars keep their shape but sum
  // to the real figures.
  const generateDummyChart = (labels: string[]) => {
    const spike = [1.3, 0.85, 1.6, 0.9, 1.9, 1.1];
    const weights = labels.map(
      (_, i) => Math.pow(1.1, i) * spike[i % spike.length],
    );
    const sumW = weights.reduce((s, w) => s + w, 0);
    const distribute = (total: number) =>
      weights.map((w) => Math.round((total * w) / sumW));
    const spendingData = distribute(1840);
    const emvData = distribute(17734);
    const salesData = distribute(4600);
    return {
      labels,
      spendingData,
      emvData,
      salesData,
      totalEstimatedSpending: spendingData.reduce((s, v) => s + v, 0),
    };
  };

  const dummyData = useMemo(() => {
    if (!isDummyUser) return null;
    const labels = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
    return {
      chart: generateDummyChart(labels),
      roas: generateDummyROAS(labels),
    };
  }, [isDummyUser]);

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex items-center justify-between">
        <div
          style={{
            color: '#121111',
            fontWeight: 500,
            fontSize: 20,
            lineHeight: '120%',
            letterSpacing: 0,
            opacity: 0.9,
          }}
        >
          {t('common.analytics')}
        </div>
        <CalendarFilter
          onDateRangeChange={onDateFilterChange}
          defaultFilter="all-time"
        />
      </div>

      <ValueVsSpendComponent metrics={valueVsSpend} />

      <CampaignOverViewComponent
        variant="individual"
        metrics={chartResponse}
        deltas={chartResponse?.deltas}
      />

      <ChartAnaylysisComponent
        earnedMedia={
          isDummyUser
            ? {
                labels: dummyData?.chart.labels,
                emvData: dummyData?.chart.emvData,
                salesData: dummyData?.chart.salesData,
                spendingData: dummyData?.chart.spendingData,
                totalEmv: (dummyData?.chart.emvData || []).reduce(
                  (s, v) => s + v,
                  0,
                ),
              }
            : chartResponse
        }
        roas={
          isDummyUser
            ? {
                labels: dummyData?.roas.labels,
                allRoas: dummyData?.roas.allRoas,
                instagramRoas: dummyData?.roas.instagramRoas,
                tiktokRoas: dummyData?.roas.tiktokRoas,
                averageRoasOfTotal: dummyData?.roas.averageRoasOfTotal,
                averageRoasOfInstagram: dummyData?.roas.averageRoasOfInstagram,
                averageRoasOfTiktok: dummyData?.roas.averageRoasOfTiktok,
              }
            : roasResponse
        }
      />
    </div>
  );
};

export default AnalyticsComponent;
